/**
 * Registrera ett nytt kundprojekt. Öppnas i snabbläge (en skärm, under samtal):
 *   namn · kundens roll · byggskede · säkerhetsskydd (+ IT-miljö och
 *   projektkontor när säkerhetsskydd är ja)
 * "Fler fält" visar hela formuläret i samma dialog.
 *
 * Kunden (och kontakten, leadet, aktiviteten) man står på kopplas automatiskt
 * som första part. Fler parter läggs till när kundprojektet redigeras.
 * Fälten är desamma som i redigeringen (KundprojektFalt.jsx).
 *
 * Kundprojekt är inte Tikabs uppdrag eller referensprojekt – se kundprojekt.js.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, Alert, Link, Divider,
} from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import {
  tomtKundprojekt, nyPart, valideraKundprojekt, visaSakerhetsvarning, SAKERHET_VARNING, KP_BYGGSKEDEN, KP_ROLLER,
} from './kundprojekt';
import { sparaKundprojekt } from './kundprojektData';
import { NamnFalt, GrunddataFalt, SakerhetFalt, Val, FaltRubrik } from './KundprojektFalt';

/** Aktivitetstyp → kundprojektets källa. */
const KALLA_FRAN_AKTIVITET = { phone: 'samtal', meeting: 'mote', email: 'mejl' };

/**
 * @param {object} p
 *   open, onClose, onSparad(resultat)
 *   kund       { id, name }  – kunden man står på
 *   kontakt    { id, name }  – kontakten man står på (frivillig)
 *   leadId     leadet man står på (frivilligt)
 *   aktivitet  { typ, leadId, aktivitetId } – när det registreras från en aktivitet
 *   uid
 */
export default function KundprojektDialog({ open, onClose, onSparad, kund, kontakt, leadId, aktivitet, uid }) {
  const [kp, setKp] = useState(() => tomtKundprojekt({
    kalla: aktivitet ? (KALLA_FRAN_AKTIVITET[aktivitet.typ] || 'annat') : '',
    registreradVid: aktivitet ? { leadId: aktivitet.leadId || '', aktivitetId: aktivitet.aktivitetId || '' } : null,
    parter: [nyPart(kund.id, '', {
      kontakter: kontakt ? [{ kontaktId: kontakt.id, rollIProjektet: '' }] : [],
      leadIds: leadId ? [leadId] : [],
    })],
  }));
  const [fler, setFler] = useState(false);
  const [sparar, setSparar] = useState(false);
  const [fel, setFel] = useState('');

  const satt = (falt, varde) => setKp(k => ({ ...k, [falt]: varde }));
  const part = kp.parter[0];
  const sattPart = andring => setKp(k => ({ ...k, parter: [{ ...k.parter[0], ...andring }, ...k.parter.slice(1)] }));

  async function spara() {
    const valideringsfel = valideraKundprojekt(kp);
    if (valideringsfel.length) { setFel(valideringsfel.join(' ')); return; }
    setSparar(true);
    setFel('');
    try {
      const resultat = await sparaKundprojekt(kp, uid);
      onSparad?.(resultat);
      onClose();
    } catch (e) {
      console.error('Kunde inte spara kundprojektet', e);
      setFel(e.message || String(e));
    } finally {
      setSparar(false);
    }
  }

  return (
    <Dialog open={open} onClose={sparar ? undefined : onClose} maxWidth={fler ? 'md' : 'sm'} fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <ConstructionIcon sx={{ color: '#FF6B00' }} />
        <Box>
          <Typography variant="h6" fontWeight="bold">Nytt kundprojekt</Typography>
          <Typography variant="caption" color="text.secondary">
            {[kund.name, kontakt?.name].filter(Boolean).join(' · ')} kopplas automatiskt
            {leadId ? ', liksom leadet' : ''}
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <NamnFalt kp={kp} satt={satt} autoFocus />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Val label={`${kund.name || 'Kundens'} roll`} lista={KP_ROLLER} tom="Välj roll"
              value={part.roll} onChange={roll => sattPart({ roll })} />
            <Val label="Byggskede" lista={KP_BYGGSKEDEN} value={kp.byggskede} onChange={x => satt('byggskede', x)} />
          </Box>
          <SakerhetFalt kp={kp} satt={satt} full={fler} />
          {fler && (
            <>
              <FaltRubrik>Grunddata</FaltRubrik>
              <GrunddataFalt kp={kp} satt={satt} />
              {kontakt && (
                <TextField size="small" fullWidth label={`${kontakt.name}s roll i projektet`}
                  value={part.kontakter[0]?.rollIProjektet || ''}
                  helperText={visaSakerhetsvarning(kp) ? SAKERHET_VARNING : undefined}
                  onChange={e => sattPart({ kontakter: [{ kontaktId: kontakt.id, rollIProjektet: e.target.value }] })} />
              )}
            </>
          )}
          {!fler && (
            <Link component="button" type="button" variant="body2" onClick={() => setFler(true)}
              sx={{ alignSelf: 'flex-start', color: '#FF6B00' }}>
              Fler fält
            </Link>
          )}
          {fel && <Alert severity="error">{fel}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={sparar} sx={{ color: 'text.secondary' }}>Avbryt</Button>
        <Button variant="contained" onClick={spara} disabled={sparar || !kp.namn.trim()}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 700 }}>
          {sparar ? 'Sparar…' : 'Spara kundprojekt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
